import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { clearPortalQueryCache } from '../utils/portalQueryCache';

const AuthContext = createContext(undefined);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const lastUserSignatureRef = useRef('');
  const [provider, setProvider] = useState({
    provider: 'LocalDemo',
    authMode: 'Local',
    loginUrl: '/api/auth/login',
    logoutUrl: '/api/auth/logout',
    tenantUrl: '',
    kinde: { enabled: false, scopes: [] },
  });

  const loadProvider = useCallback(async () => {
    const response = await fetch('/api/auth/provider-config', { credentials: 'include' });
    if (!response.ok) {
      return null;
    }

    const config = await response.json();
    setProvider(config);
    return config;
  }, []);

  const refresh = useCallback(async () => {
    try {
      const response = await fetch('/api/auth/me', { credentials: 'include' });
      if (!response.ok) {
        setUser(null);
        lastUserSignatureRef.current = '';
        clearPortalQueryCache();
        return null;
      }

      const profile = await response.json();
      const nextSignature = `${(profile?.email || '').trim().toLowerCase()}|${(profile?.displayName || '').trim()}|${(profile?.role || '').trim()}`;
      if (lastUserSignatureRef.current && lastUserSignatureRef.current !== nextSignature) {
        clearPortalQueryCache();
      }
      lastUserSignatureRef.current = nextSignature;
      setUser(profile);
      return profile;
    } catch {
      // Fail closed: if central auth status cannot be verified, drop local auth state.
      setUser(null);
      lastUserSignatureRef.current = '';
      clearPortalQueryCache();
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    Promise.allSettled([loadProvider(), refresh()]).finally(() => setIsLoading(false));
  }, [loadProvider, refresh]);

  useEffect(() => {
    if (!user) return undefined;
    const poll = async () => {
      if (document.hidden) return;
      try {
        await refresh();
      } catch {
        // ignore transient refresh errors
      }
    };
    const id = window.setInterval(poll, 20_000);
    const onVis = () => {
      if (!document.hidden) void poll();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [user, refresh]);

  const login = useCallback(async (email, password, returnUrl) => {
    if (provider?.kinde?.enabled) {
      throw new Error('Kinde login must be started with redirect.');
    }

    const response = await fetch('/api/auth/login', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, returnUrl }),
    });

    if (!response.ok) {
      throw new Error('Sign-in failed. Please check your email and password.');
    }

    const result = await response.json();
    setUser(result.profile);
    return result.redirectUrl;
  }, [provider?.kinde?.enabled]);

  const startProviderLogin = useCallback((returnUrl, options = {}) => {
    const safeReturnUrl = returnUrl || '/tools';
    const query = new URLSearchParams({ returnUrl: safeReturnUrl });
    if (options.loginHint) {
      query.set('loginHint', options.loginHint);
    }
    if (options.connectionId) {
      query.set('connectionId', options.connectionId);
    }
    if (options.disableConnectionId) {
      query.set('disableConnectionId', 'true');
    }
    if (options.prompt) {
      query.set('prompt', options.prompt);
    } else if (options.forcePromptLogin) {
      query.set('prompt', 'login');
    }
    if (options.maxAge !== undefined && options.maxAge !== null) {
      query.set('maxAge', String(options.maxAge));
    }

    const target = `${provider.loginUrl}?${query.toString()}`;
    window.location.assign(target);
  }, [provider]);

  const logout = useCallback(async (returnUrl) => {
    const response = await fetch('/api/auth/logout', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ returnUrl }),
    });

    setUser(null);
    lastUserSignatureRef.current = '';
    clearPortalQueryCache();

    if (!response.ok) {
      return '/login';
    }

    const result = await response.json();
    return result.redirectUrl || '/login';
  }, []);

  const value = useMemo(
    () => ({
      user,
      isAuthenticated: Boolean(user),
      isLoading,
      provider,
      login,
      startProviderLogin,
      logout,
      refresh,
    }),
    [user, isLoading, provider, login, startProviderLogin, logout, refresh]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used inside AuthProvider');
  }

  return context;
}
